import { ArrowUturnLeftIcon, LinkIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { FC, ReactNode } from "react";
import { cx } from "../../utils/classnames";
import Button from "../ui/Button";
import InfoTip from "../ui/InfoTip";

// Where the feed takes a GTFS stop field from:
// "station"  the field follows the station and updates by itself when the station changes;
// "pending"  it follows the station now, but the form holds a change that will mark it edited on save;
// "edited"   it was set for the feed and no longer follows the station.
export type StopFieldSourceState = "station" | "pending" | "edited";

type Pill = { label: string; className: string; Icon: typeof LinkIcon };

const EDITED_PILL = "bg-[#FEF3C7] text-[#B45309]";

const PILL: Record<StopFieldSourceState, Pill> = {
  station: { label: "Ndjek stacionin", className: "bg-gray-100 text-gray-600", Icon: LinkIcon },
  pending: { label: "Do të ndryshohet për GTFS", className: EDITED_PILL, Icon: PencilSquareIcon },
  edited: { label: "E ndryshuar për GTFS", className: EDITED_PILL, Icon: PencilSquareIcon }
};

const TIP: Record<StopFieldSourceState, string> = {
  station:
    "Kjo vlerë vjen nga stacioni dhe përditësohet vetë kur ndryshon stacioni. " +
    "Nëse e ndryshoni këtu, GTFS do të përdorë vlerën tuaj.",
  pending:
    "Pas ruajtjes kjo fushë nuk ndjek më stacionin: GTFS do të përdorë vlerën që keni shkruar.",
  edited:
    "GTFS përdor vlerën e vendosur këtu dhe nuk ndjek ndryshimet e stacionit. " +
    "\"Merr nga stacioni\" e kthen te vlera e stacionit."
};

export const StopFieldSourcePill: FC<{
  state: StopFieldSourceState;
  label?: string;
  compact?: boolean;
}> = ({ state, label, compact }) => {
  const { label: stateLabel, className, Icon } = PILL[state];
  return (
    <span
      className={cx([
        "inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap",
        compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-0.5 text-xs",
        className
      ])}
    >
      <Icon className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={2} />
      {label ?? stateLabel}
    </span>
  );
};

// One group of GTFS stop fields (the name, or the latitude and longitude together) with where the feed
// takes them from and, when edited, the action that makes them follow the station again.
const StopFieldSource: FC<{
  title: string;
  state: StopFieldSourceState;
  resetLabel: string;
  onReset: () => void;
  resetting?: boolean;
  children: ReactNode;
}> = ({ title, state, resetLabel, onReset, resetting, children }) => (
  <section
    className={cx([
      "rounded-lg border p-4 grid gap-4",
      state === "station" ? "border-gray-200 bg-gray-50" : "border-[#FCD34D] bg-white"
    ])}
  >
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <StopFieldSourcePill state={state} />
        <InfoTip text={TIP[state]} />
      </div>
      {state === "edited" && (
        <Button
          btnType="custom"
          padding="px-3 py-1.5"
          className="inline-flex items-center gap-1.5 border border-primary text-primary"
          disabled={resetting}
          onClick={onReset}
        >
          <ArrowUturnLeftIcon className="w-4 h-4" strokeWidth={2} />
          {resetLabel}
        </Button>
      )}
    </div>
    {children}
  </section>
);

export default StopFieldSource;
